import { api, httpClient } from './api';
import { grundHeader } from '../components/ui/loeschen-mit-grund-dialog';

export interface RoomFile {
  url: string;
  name?: string | null;
}

export interface Room {
  id: number;
  standort_id: number;
  name: string;
  kapazitaet: number | null;
  aktiv: boolean;
  status?: 'draft' | 'active' | 'inactive';
  wizard_step?: number | null;
  internal_note?: string | null;
  not_public_accessible?: boolean;
  address?: string | null;
  address_street?: string | null;
  address_house_number?: string | null;
  address_postal_code?: string | null;
  address_city?: string | null;
  directions?: string | null;
  image_url?: string | null;
  images?: RoomFile[];
  documents?: RoomFile[];
  size_m2?: number | null;
  cost_cents?: number | null;
  cost_interval?: 'hour' | 'day' | 'usage' | 'month' | null;
  tag_ids?: number[];
  tags?: string[];
  erstellt_am?: string;
}

export interface RoomCreateData {
  standort_id: number;
  name: string;
  kapazitaet?: number;
  aktiv?: boolean;
  status?: 'draft' | 'active' | 'inactive';
  wizard_step?: number | null;
  internal_note?: string | null;
  not_public_accessible?: boolean;
  address?: string | null;
  address_street?: string | null;
  address_house_number?: string | null;
  address_postal_code?: string | null;
  address_city?: string | null;
  directions?: string | null;
  image_url?: string | null;
  images?: RoomFile[];
  documents?: RoomFile[];
  size_m2?: number | null;
  cost_cents?: number | null;
  cost_interval?: 'hour' | 'day' | 'usage' | 'month' | null;
  tag_ids?: number[];
}

export interface RoomUpdateData {
  standort_id?: number;
  name?: string;
  kapazitaet?: number;
  aktiv?: boolean;
  status?: 'draft' | 'active' | 'inactive';
  wizard_step?: number | null;
  internal_note?: string | null;
  not_public_accessible?: boolean;
  address?: string | null;
  address_street?: string | null;
  address_house_number?: string | null;
  address_postal_code?: string | null;
  address_city?: string | null;
  directions?: string | null;
  image_url?: string | null;
  images?: RoomFile[];
  documents?: RoomFile[];
  size_m2?: number | null;
  cost_cents?: number | null;
  cost_interval?: 'hour' | 'day' | 'usage' | 'month' | null;
  tag_ids?: number[];
}

/**
 * Alle Räume abrufen
 */
export const getRooms = async (
  standortId?: number,
  opts?: { tagIds?: number[]; minSize?: number; maxSize?: number }
): Promise<Room[]> => {
  const params: string[] = [];
  if (standortId) params.push(`standort_id=${standortId}`);
  if (opts?.tagIds && opts.tagIds.length > 0) params.push(`tag_ids=${opts.tagIds.join(',')}`);
  if (typeof opts?.minSize === 'number') params.push(`min_size=${opts.minSize}`);
  if (typeof opts?.maxSize === 'number') params.push(`max_size=${opts.maxSize}`);
  const url = params.length > 0 ? `/api/rooms?${params.join('&')}` : '/api/rooms';
  
  const response = await httpClient<Room[]>(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
  
  return response.data || [];
};

/**
 * Einzelnen Raum abrufen
 */
export const getRoom = async (id: number): Promise<Room> => {
  const response = await httpClient<Room>(`/api/rooms/${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
  
  if (!response.data) {
    throw new Error('Raum nicht gefunden');
  }
  
  return response.data;
};

/**
 * Neuen Raum erstellen
 */
export const createRoom = async (data: RoomCreateData): Promise<Room> => {
  const response = await httpClient<Room>('/api/rooms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
  
  if (!response.data) {
    throw new Error('Raum konnte nicht erstellt werden');
  }
  
  return response.data;
};

/**
 * Raum aktualisieren
 */
export const updateRoom = async (id: number, data: RoomUpdateData): Promise<Room> => {
  const response = await httpClient<Room>(`/api/rooms/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
  
  if (!response.data) {
    throw new Error('Raum konnte nicht aktualisiert werden');
  }
  
  return response.data;
};

/**
 * Raum löschen
 */
export const deleteRoom = async (id: number, grund: string): Promise<void> => {
  const response = await httpClient<{ message: string }>(`/api/rooms/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...grundHeader(grund).headers },
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
};

// Room equipment tags API
export interface RoomTag { id: number; name: string; is_active?: number | boolean; created_at?: string }

export const getRoomTags = async (): Promise<RoomTag[]> => {
  const response = await httpClient<RoomTag[]>('/api/room-tags', { method: 'GET', headers: { 'Content-Type': 'application/json' } });
  if (response.error) throw new Error(response.error);
  return response.data || [];
};

export const createRoomTag = async (name: string): Promise<RoomTag> => {
  const response = await httpClient<RoomTag>('/api/room-tags', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, is_active: true }),
  });
  if (response.error || !response.data) throw new Error(response.error || 'Fehler beim Erstellen des Tags');
  return response.data;
};

export const updateRoomTag = async (id: number, payload: { name?: string; is_active?: boolean }): Promise<RoomTag> => {
  const response = await httpClient<RoomTag>(`/api/room-tags/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (response.error || !response.data) throw new Error(response.error || 'Fehler beim Aktualisieren des Tags');
  return response.data;
};

export const deleteRoomTag = async (id: number): Promise<void> => {
  const response = await httpClient<{ message: string }>(`/api/room-tags/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.error) throw new Error(response.error);
};

// Upload helper for room images
const uploadRoomFile = async (endpoint: string, file: File): Promise<RoomFile> => {
  const form = new FormData();
  form.append('file', file);
  // api.upload haengt API_BASE_URL und den Bearer-Token an — ohne beides gab es 401.
  const json = await api.upload<{ url: string; name?: string }>(endpoint, form);
  return { url: json.url, name: json.name || file.name };
};

export const uploadRoomImage = async (file: File): Promise<RoomFile> =>
  uploadRoomFile('/api/uploads/room-image', file);

export const uploadRoomDocument = async (file: File): Promise<RoomFile> =>
  uploadRoomFile('/api/uploads/room-document', file);

/**
 * Unterrichtseinheit für Raumbelegung
 */
export interface RoomUnit {
  id: number;
  course_id: number | null;
  rule_id: number | null;
  override_id: number | null;
  lehrer_id: number | null;
  schueler_id: number | null;
  fach_id: number;
  raum_id: number | null;
  start_zeit: string; // YYYY-MM-DD HH:MM:SS
  end_zeit: string;   // YYYY-MM-DD HH:MM:SS
  status: 'geplant' | 'durchgeführt' | 'nicht_erschienen' | 'abgesagt' | 'verschoben' | 'abgeschlossen';
  // „course_pattern“: Termin nur aus der Wochenregel, ohne eigene Zeile (T-0187).
  source_type: 'course' | 'trial' | 'course_pattern';
  // Erweiterte Felder aus JOINs
  kurs_name?: string | null;
  fach_name?: string | null;
  lehrer_name?: string | null;
}

/**
 * Unterrichtseinheiten für einen Raum im Zeitraum abrufen
 */
export const getRoomUnits = async (
  roomId: number,
  start: string, // YYYY-MM-DD
  end: string     // YYYY-MM-DD
): Promise<RoomUnit[]> => {
  const url = `/api/rooms/${roomId}/units?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  
  const response = await httpClient<RoomUnit[]>(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
  
  return response.data || [];
};

/**
 * Unterrichtseinheiten für mehrere Räume im Zeitraum abrufen
 */
export const getRoomsUnits = async (
  roomIds: number[],
  start: string, // YYYY-MM-DD
  end: string     // YYYY-MM-DD
): Promise<RoomUnit[]> => {
  if (roomIds.length === 0) return [];
  
  const roomIdsParam = roomIds.join(',');
  const url = `/api/rooms/units?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&roomIds=${roomIdsParam}`;
  
  const response = await httpClient<RoomUnit[]>(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (response.error) {
    throw new Error(response.error);
  }
  
  return response.data || [];
};

/**
 * Freie Raumbuchung (Elternabend, Probe, Wartung …) — ohne Unterricht.
 * Zeiten sind Wandzeit: „2026-10-05T16:00:00“ ohne Zeitzone.
 */
export interface Raumbelegung {
  id: number;
  raum_id: number;
  kurs_id?: number | null;
  person_id?: number | null;
  title: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
}

export interface RaumbelegungInput {
  raum_id: number;
  title: string;
  starts_at: string;
  ends_at: string;
}

export const raumbelegungenApi = {
  list: async (from: string, to: string): Promise<Raumbelegung[]> => {
    const params = new URLSearchParams({ from: `${from}T00:00:00`, to: `${to}T23:59:59`, limit: '200' });
    const data = await api.get<{ items: Raumbelegung[] }>(`/api/raumbelegungen?${params.toString()}`);
    return Array.isArray(data?.items) ? data.items : [];
  },
  create: (payload: RaumbelegungInput) =>
    api.post<Raumbelegung>('/api/raumbelegungen', { ...payload, status: 'booked' }),
  update: (id: number, payload: Partial<RaumbelegungInput>) =>
    api.patch<Raumbelegung>(`/api/raumbelegungen/${id}`, payload),
  delete: (id: number) => api.delete<void>(`/api/raumbelegungen/${id}`),
};
